/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { Injectable, inject } from '@angular/core';
import { AppConfig } from '../engine/app-config';
import { DataModel } from '../engine/data-model';
import { GroupEntity } from '../tool/group-entity';
import { PdfExportTool } from './pdf-export-tool';
import { CsvExportTool } from './csv-export-tool';
import { PdfEntity } from '../model/entity/pdf-entity';
import { Observable, catchError, map, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ModelProperty } from '../engine/model-property';
import { IEntity } from '../interface/IEntity';
import dayjs from 'dayjs';
import { RestService } from '../engine/http/rest.service';
import { ObjectFactory } from '../engine/http/factory/object-factory';
import { ServerExportResultDto } from '../model/data-table/data-table-server-export';

@Injectable({
  providedIn: 'root',
})
/**
 * Service used to export data on pdf or csv
 *
 */
export class ExportService {
  appConfig = inject(AppConfig);
  private trl = inject(TranslateService);
  private restService = inject(RestService);


  /**
   * Export array to pdf
   */
  public exportArrayToPDF(
    model: DataModel,
    columns: ModelProperty[],
    data: any[],
    orientation?: 'l' | 'p',
    detailView?: boolean,
    title?: string,
    author?: string,
    id?: string,
    directDownload = true
  ): Observable<string> {
    let formatTitle = title ? title : model.title;
    if (!formatTitle) {
      formatTitle = this.appConfig.appName;
    }
    formatTitle = this.buildTitle(formatTitle);
    const formatAuthor = author ? author : this.appConfig.connectedUser;
    const exportLogo = this.appConfig.exportLogoPath
      ? this.appConfig.exportLogoPath
      : this.appConfig.appLogoPath;
    const pdfTool = new PdfExportTool(
      this.trl,
      exportLogo,
      formatTitle,
      formatAuthor,
      model,
      columns,
      id,
      orientation
    );
    return pdfTool.exportArray(data, detailView, false, directDownload);
  }

  /**
   * Export group array to pdf
   */
  public exportGroupArrayToPDF(
    model: DataModel,
    columns: ModelProperty[],
    data: GroupEntity<IEntity>[],
    orientation?: 'l' | 'p',
    detailView?: boolean,
    title?: string,
    author?: string,
    id?: string
  ) {
    let docTitle = title ? title : model.title;
    if (!docTitle) {
      docTitle = this.appConfig.appName;
    }
    const formatTitle = this.buildTitle(docTitle);
    const formatAuthor = author ? author : this.appConfig.connectedUser;
    const exportLogo = this.appConfig.exportLogoPath
      ? this.appConfig.exportLogoPath
      : this.appConfig.appLogoPath;
    const pdfTool = new PdfExportTool(
      this.trl,
      exportLogo,
      formatTitle,
      formatAuthor,
      model,
      columns,
      id,
      orientation
    );
    pdfTool.exportArray(data, detailView, true);
  }

  public exportEntityToPdf(
    model: DataModel,
    entity: PdfEntity,
    columns: ModelProperty[]
  ) {
    const formatTitle = entity.getPdfTitle();
    const formatAuthor = entity.getPdfSubtitle();
    const id = entity.getPdfId();
    const exportLogo = this.appConfig.exportLogoPath
      ? this.appConfig.exportLogoPath
      : this.appConfig.appLogoPath;
    const pdfTool = new PdfExportTool(
      this.trl,
      exportLogo,
      formatTitle,
      formatAuthor,
      model,
      columns,
      id
    );
    pdfTool.exportEntity(entity);
  }

  /**
   * Export array to Csv
   */
  public exportArrayToCSV(
    model: DataModel,
    columns: ModelProperty[],
    data: any[],
    params?: {
      title?: string;
      author?: string;
      directDownload?: boolean;
      displayColumnsGroups: boolean;
    }
  ) {
    let formatTitle = params.title ? params.title : model.title;
    if (!formatTitle) {
      formatTitle = this.appConfig.appName;
    }
    formatTitle = this.buildTitle(formatTitle);
    const formatAuthor = params.author
      ? params.author
      : this.appConfig.connectedUser;
    const csvTool = new CsvExportTool(
      model,
      columns,
      formatAuthor,
      formatTitle
    );
    return csvTool.export(
      data,
      params.directDownload,
      params.displayColumnsGroups
    );
  }

  /**
   * Ask the server to create an export file, then download it
   * @param url : api creating the file ({@see RestService.postJob}), its result is a {@see ServerExportResultDto}
   * @return true when the download has been started
   */
  public exportFromServer$(url: string): Observable<boolean> {
    return this.restService
      .postJob<ServerExportResultDto>(url, {}, { showLoading: false })
      .pipe(
        map((result) => {
          const link = result?.data?.links?.download;
          if (!link) {
            return false;
          }
          this.download(link);
          return true;
        }),
        // The error is already displayed by RestService
        catchError(() => of(false)),
      );
  }

  private download(url: string) {
    // The server sends the download headers: the browser keeps the current page
    const a = document.createElement('a');
    a.href = this.restService.getUrl(new ObjectFactory(url));
    a.style.display = 'none';
    document.body.append(a);
    a.click();
    a.remove();
  }

  protected buildTitle(title: string) {
    return `${dayjs(new Date()).format('YYYY-MM-DD')}-${title}`;
  }
}
